import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Swal from 'sweetalert2';

import {
  BRANDS_UPDATE_MULTI,
  DUPLICATE_UNVERIFIED_BRANDS_GET_DUPLICATE_UNVERIFIED_BRAND_CLUSTERS,
  DUPLICATE_UNVERIFIED_BRANDS_HANDLE_DUPLICATE_BRAND_CLUSTERS,
} from 'lib/networking/endpoints';
import { BrandVerificationStatus } from 'lib/enums/BrandVerificationStatus';
import Container from 'components/shared/Container';
import DuplicateBrandsActionsSection from 'components/manage_brands/DuplicateBrandsActionsSection';
import DuplicateBrandsTable from 'components/manage_brands/DuplicateBrandsTable';
import DuplicateBrandsProductsModal from 'components/manage_brands/DuplicateBrandsProductsModal';
import MergeBrandsModal from 'components/manage_brands/MergeBrandsModal';
import { ONE_PAGE_SIZE } from 'lib/constants';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function DuplicateUnverifiedBrandsView({ selectedBrand, selectedVendor }) {
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeOrDeleteList, setMergeOrDeleteList] = useState([]);
  const [currentPage, setCurrentPage] = useState(ONE_PAGE_SIZE);
  const [selectedBrandForProducts, setSelectedBrandForProducts] = useState(null);
  const [showBrandProductsModal, setShowBrandProductsModal] = useState(false);

  const {
    data: { data: duplicateBrandClusters = [] } = {},
    loading: loadingDuplicateBrandClusters,
    refetch: refetchDuplicateBrandClusters,
  } = useGet(DUPLICATE_UNVERIFIED_BRANDS_GET_DUPLICATE_UNVERIFIED_BRAND_CLUSTERS, {
    verified_brand_id: selectedBrand?.value || null,
    global_vendor_id: selectedVendor?.value || null,
    limit: ONE_PAGE_SIZE,
    offset: ONE_PAGE_SIZE * (currentPage - 1),
  });

  const {
    data: { count: duplicateBrandClustersCount = 0 } = {},
    loading: loadingDuplicateBrandClustersCount,
    refetch: refetchDuplicateBrandClustersCount,
  } = useGet(DUPLICATE_UNVERIFIED_BRANDS_GET_DUPLICATE_UNVERIFIED_BRAND_CLUSTERS, {
    verified_brand_id: selectedBrand?.value || null,
    global_vendor_id: selectedVendor?.value || null,
    limit: ONE_PAGE_SIZE,
    offset: ONE_PAGE_SIZE * (currentPage - 1),
    count_only: true,
  });

  const { postData: handleDuplicateBrandClusters, loading: handlingDuplicateBrandClusters } =
    usePost(
      DUPLICATE_UNVERIFIED_BRANDS_HANDLE_DUPLICATE_BRAND_CLUSTERS,
      () => {
        Swal.fire({
          icon: 'success',
          text: 'Duplicate brand clusters handled successfully',
        });
        refetchDuplicateBrandClusters();
        setMergeOrDeleteList([]);
      },
      error => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: `An error occurred: ${error?.response?.data?.message}`,
        });
        setMergeOrDeleteList([]);
      },
    );

  const { postData: updateBrands, loading: updatingBrands } = usePost(
    BRANDS_UPDATE_MULTI,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Brands unverified successfully',
        title: 'Success',
        position: 'center',
      });
      refetchDuplicateBrandClusters();
      refetchDuplicateBrandClustersCount();
      setMergeOrDeleteList([]);
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      });
      setMergeOrDeleteList([]);
    },
  );

  useEffect(() => {
    setCurrentPage(ONE_PAGE_SIZE);
    setMergeOrDeleteList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor]);

  const isBrandSelected = brand => {
    if (!brand) return false;
    return mergeOrDeleteList.some(selectedBrandItem => selectedBrandItem?.id === brand.id);
  };

  const toggleBrandSelected = (brand, checked) => {
    if (!brand?.id) return;
    setMergeOrDeleteList(prev => {
      const alreadySelected = prev.some(selectedBrandItem => selectedBrandItem?.id === brand.id);

      if (checked) {
        if (alreadySelected) return prev;
        return [...prev, brand];
      }

      if (!alreadySelected) return prev;
      return prev.filter(selectedBrandItem => selectedBrandItem?.id !== brand.id);
    });
  };

  const currentPageBrands = [
    duplicateBrandClusters[0]?.verified_brand,
    ...(duplicateBrandClusters || []).map(cluster => cluster?.unverified_brand),
  ].filter(brand => brand?.id);

  const getUniqueBrandsById = brands => {
    const map = new Map();
    brands.forEach(brand => {
      if (brand?.id && !map.has(brand.id)) {
        map.set(brand.id, brand);
      }
    });
    return Array.from(map.values());
  };

  const handleMarkClusterAsCompleted = () => {
    handleDuplicateBrandClusters({
      unverified_brand_ids: duplicateBrandClusters
        .map(cluster => cluster?.unverified_brand?.id)
        .filter(Boolean),
    });
  };

  const currentPageUniqueBrands = getUniqueBrandsById(currentPageBrands);

  const areAllOnPageSelected = () =>
    currentPageUniqueBrands.length > 0 &&
    currentPageUniqueBrands.every(brand => isBrandSelected(brand));

  const areSomeOnPageSelected = () =>
    currentPageUniqueBrands.some(brand => isBrandSelected(brand)) && !areAllOnPageSelected();

  const handleSelectAllOnPage = e => {
    const { checked } = e.target;

    setMergeOrDeleteList(prev => {
      const prevById = new Map((prev || []).filter(b => b?.id).map(b => [b.id, b]));

      if (checked) {
        currentPageUniqueBrands.forEach(brand => {
          if (!prevById.has(brand.id)) prevById.set(brand.id, brand);
        });
        return Array.from(prevById.values());
      }

      currentPageUniqueBrands.forEach(brand => {
        prevById.delete(brand.id);
      });
      return Array.from(prevById.values());
    });
  };

  return (
    <Container>
      <DuplicateBrandsActionsSection
        updateBrands={updateBrands}
        mergeOrDeleteList={mergeOrDeleteList}
        setShowMergeModal={setShowMergeModal}
        refetchDuplicateBrandClusters={refetchDuplicateBrandClusters}
        refetchDuplicateBrandClustersCount={refetchDuplicateBrandClustersCount}
        handleMarkClusterAsCompleted={handleMarkClusterAsCompleted}
        currentPage={currentPage}
        duplicateBrandClustersCount={duplicateBrandClustersCount}
        loadingDuplicateBrandClusters={loadingDuplicateBrandClusters}
        setCurrentPage={setCurrentPage}
        setMergeOrDeleteList={setMergeOrDeleteList}
      />
      <DuplicateBrandsTable
        areAllOnPageSelected={areAllOnPageSelected}
        handleSelectAllOnPage={handleSelectAllOnPage}
        areSomeOnPageSelected={areSomeOnPageSelected}
        loadingDuplicateBrandClusters={loadingDuplicateBrandClusters}
        brands={[
          duplicateBrandClusters[0]?.verified_brand,
          ...duplicateBrandClusters.map(cluster => cluster?.unverified_brand).filter(Boolean),
        ].filter(Boolean)}
        updatingBrands={updatingBrands}
        toggleBrandSelected={toggleBrandSelected}
        isBrandSelected={isBrandSelected}
        setSelectedBrandForProducts={setSelectedBrandForProducts}
        setShowBrandProductsModal={setShowBrandProductsModal}
      />
      {showMergeModal ? (
        <MergeBrandsModal
          mergeList={mergeOrDeleteList}
          setMergeList={setMergeOrDeleteList}
          onHide={() => {
            setShowMergeModal(false);
          }}
          onMerge={() => {
            handleDuplicateBrandClusters({
              unverified_brand_ids: mergeOrDeleteList
                .filter(brand => brand.verification_status === BrandVerificationStatus.UNVERIFIED)
                .map(brand => brand.id),
            });
            refetchDuplicateBrandClusters();
            setMergeOrDeleteList([]);
          }}
        />
      ) : null}
      {showBrandProductsModal ? (
        <DuplicateBrandsProductsModal
          brand={selectedBrandForProducts}
          onHide={() => setShowBrandProductsModal(false)}
        />
      ) : null}
    </Container>
  );
}

DuplicateUnverifiedBrandsView.propTypes = {
  selectedBrand: PropTypes.object.isRequired,
  selectedVendor: PropTypes.object.isRequired,
};

export default DuplicateUnverifiedBrandsView;
